import {useState, useEffect, type Dispatch} from 'react';
import backendCommunicator from 'services/backendCommunicator';
import {type AxiosError} from 'axios';

import Alert from 'helpers/Alert';

import * as I from 'interfaces';
import useParseURL from './useParseURL';

const deleteHandler = async ({planId, id, type}: {planId: string; id: string; type: I.FormSelectionEnum}) => {
  switch (type) {
    case I.FormSelectionEnum.plan:
      return await backendCommunicator.deletePlan(id);
    case I.FormSelectionEnum.site:
      return await backendCommunicator.deleteSite(id);
    case I.FormSelectionEnum.cage:
      return await backendCommunicator.deleteCage(id);
    case I.FormSelectionEnum.fallowing:
      return await backendCommunicator.deleteSiteFallowing(id);
    case I.FormSelectionEnum.input:
      return await backendCommunicator.deleteCageInput({planId, inputId: id});
    case I.FormSelectionEnum.fishGroupProperties:
      return await backendCommunicator.deleteFishGroupProperty({planId, fishPropertyId: id});
    case I.FormSelectionEnum.harvest:
      return await backendCommunicator.deleteCageHarvest(id);
    case I.FormSelectionEnum.emergencyHarvest:
      return await backendCommunicator.deleteCageHarvest(id);
    case I.FormSelectionEnum.concession:
      return await backendCommunicator.deleteConcessions(id);
    case I.FormSelectionEnum.growthModel:
      return await backendCommunicator.deleteGrowthModel(planId, id);
    case I.FormSelectionEnum.fishType:
      return await backendCommunicator.deleteFishType(planId, id);
    case I.FormSelectionEnum.siteGrowthModel:
      return await backendCommunicator.deleteSiteGrowthModelOverride(id);
    case I.FormSelectionEnum.transfer:
      return await backendCommunicator.deleteTransfer(id);
    case I.FormSelectionEnum.transferIn:
      return await backendCommunicator.deleteTransfer(id);
    case I.FormSelectionEnum.transferOut:
      return await backendCommunicator.deleteTransfer(id);
    case I.FormSelectionEnum.smoltAvailability:
      return await backendCommunicator.deleteSmoltSupply(planId, id);
    case I.FormSelectionEnum.inventoryData:
      return await backendCommunicator.deleteProductionHistory();
    case I.FormSelectionEnum.priceModel:
      return await backendCommunicator.deletePriceModel(id);
    case I.FormSelectionEnum.fcrModel:
      return await backendCommunicator.deleteFCRModel(planId, id);
    case I.FormSelectionEnum.harvestDemand:
      return await backendCommunicator.deleteHarvestDemand(planId, id);
    case I.FormSelectionEnum.liveFishTransaction:
      return await backendCommunicator.deleteLiveFishTransaction(planId, id);
    case I.FormSelectionEnum.delousing:
      return await backendCommunicator.deleteDelousing(id);
    case I.FormSelectionEnum.harvestPlant:
      return await backendCommunicator.deleteHarvestPlant(id);
    case I.FormSelectionEnum.region:
      return await backendCommunicator.deleteRegion(id);
    case I.FormSelectionEnum.priceModelAssignment:
      return await backendCommunicator.deletePriceModelAssignment(id);
    case I.FormSelectionEnum.folder:
      return await backendCommunicator.deleteFolder(id);
    case I.FormSelectionEnum.qualityGradingsModel:
      return await backendCommunicator.deleteQualityGrading(id);
    case I.FormSelectionEnum.qualityModelAssignment:
      return await backendCommunicator.deleteQualityGradingModelAssignment(id);
    case I.FormSelectionEnum.mortalityModel:
      return await backendCommunicator.deleteMortalityModel(id);
    case I.FormSelectionEnum.mortalityModelAssignment:
      return await backendCommunicator.deleteMortalityModelAssignment(id);
    case I.FormSelectionEnum.company:
      return await backendCommunicator.deleteCompany(id);
    default:
      throw new Error(`Deleting ${type} is not possible.`);
  }
};

function useDelete({
  type,
  id,
  method,
  handleCommunicationSucces,
  inputMenuDispatch,
}: {
  type?: I.FormSelectionEnum;
  id?: string | null;
  method: I.methodType;
  handleCommunicationSucces: () => void;
  inputMenuDispatch?: Dispatch<I.InputMenuAction>;
}) {
  const [_delete, setDelete] = useState<boolean>(false);
  const {planId} = useParseURL();
  useEffect(() => {
    _delete &&
      type &&
      id &&
      method === I.methodType.delete &&
      deleteHandler({planId, id, type})
        .then(() => {
          handleCommunicationSucces();
          inputMenuDispatch && inputMenuDispatch({type: I.inputMenuActionType.closeMenu});
        })
        .catch((error: AxiosError) => {
          Alert.error(error);
        })
        .finally(() => {
          setDelete(false);
        });
  }, [_delete, id, type, method]);

  return {setDelete, _delete};
}

export default useDelete;
